import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 flex flex-col px-6 py-10">
      <div className="flex items-center justify-between">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-950 transition-colors w-fit"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <ThemeToggle />
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
        <Logo size={40} tagline animated linkTo={null} className="mb-10" />

        {sent ? (
          <div data-testid="forgot-success-message">
            <div className="w-14 h-14 rounded-2xl bg-coral-50 flex items-center justify-center mb-6">
              <MailCheck className="w-7 h-7 text-coral-500" />
            </div>
            <h1 className="font-display font-black text-3xl tracking-[-0.04em] leading-tight">
              Check your inbox.
            </h1>
            <p className="text-ink-500 mt-3 font-light leading-relaxed">
              If an account exists for <span className="font-semibold text-ink-950">{email}</span>,
              we've sent a password reset link. It expires in 60 minutes.
            </p>
            <p className="text-ink-500 mt-4 text-sm">
              Nothing arrived? Check your spam folder, or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-ink-950 font-semibold hover:underline underline-offset-4"
                data-testid="forgot-try-again-btn"
              >
                try again
              </button>.
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display font-black text-4xl tracking-[-0.04em] leading-tight">
              Forgot your password?
            </h1>
            <p className="text-ink-500 mt-2 font-light">
              No worries — enter your email and we'll send you a reset link.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5 mt-10">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full"
                data-testid="forgot-submit-btn"
              >
                {loading ? "Sending..." : (<>Send reset link <ArrowRight className="w-4 h-4" /></>)}
              </Button>
            </form>

            <p className="mt-8 text-sm text-ink-500">
              Remembered it after all?{" "}
              <Link to="/login" className="text-ink-950 font-semibold hover:underline underline-offset-4">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
